import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { ProfileContext } from '../../../context/Profile.context';
import { errors, personObj } from '../../../constants';
import { personEntityTitle } from '../../../utils/helper';
import { isIndividualComplete } from '../../../utils/completes';
import Controls from '../../Controls';
import Title from '../../Title';
import RemovePerson from '../../RemovePerson';

import { Lock, User, UserCheck, UserLock, Trash2 } from 'lucide-react';
import { Box, Button, Checkbox, Dialog, DialogContent, DialogTitle, EntityLabelGroup, EntityLabel, Label, TextField, Typography, InfoPanel, SectionTitle, Divider } from '@otcmarketsgroup/otcm-ui-lib';

const FORM_VIEW = 'formView';
const REMOVE_PERSON_VIEW = 'removePerson';

const IndividualControlPerson = ({ noControlPerson, onControlPersonAdd, onControlPersonUpdate, onControlPersonRemove }) => {
  const [profileState] = useContext(ProfileContext);
  const [dialog, setDialog] = useState(false);
  const [individualView, setIndividualView] = useState(personObj);
  const [slideView, setSlideView] = useState(FORM_VIEW);
  const [validationError, setValidationError] = useState(false);
  const controlPersons = profileState.profile.controlPersons;
  const individualsList = controlPersons && controlPersons.filter(person => !person.isCorpEntity && !person.isDeleted);
  const addTitle = 'Add Individual (Control Person)';
  const isAdd = !individualView.id || individualView && !individualView.id;
  const isRemoveView = slideView === REMOVE_PERSON_VIEW;
  const isReadOnly = profileState.readOnly;
  const dialogTitle = isAdd ? addTitle : personEntityTitle(individualView);

  const onValueChanged = (field, e) => {
    setIndividualView(prevState => ({
      ...prevState,
      [field]: e.target.value
    }));
  };

  const handleEdit = person => {
    setSlideView(FORM_VIEW);
    setIndividualView({ ...person });
    setDialog(true);
  };

  const handleRemoveClick = (person) => {
    setIndividualView(person);
    setSlideView(REMOVE_PERSON_VIEW);
    setDialog(true);
  };

  const handleRemove = (person) => {
    onControlPersonRemove(person);
    dialogClose();
  };

  const handleSubmitPerson = () => {
    setValidationError(false);

    const individual = individualView;
    const onSubmit = !individualView.id ? onControlPersonAdd : onControlPersonUpdate;

    if (!isIndividualComplete(individual)) {
      setValidationError(true);
      return;
    }

    onSubmit(individual);
    setDialog(false);
  };

  const validateComplete = person => {
    return isIndividualComplete(person);
  };

  const dialogClose = () => {
    setValidationError(false);
    setDialog(false);
  };

  const handleDialogClose = () => {
    setSlideView(FORM_VIEW);
    setIndividualView(personObj);
  };

  const renderAddNew = () => <>
    <DialogContent>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <p>
          Enter Name of Control Person.
        </p>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="First Name"
            placeholder="Enter text here"
            maxLength={75}
            showClearButton
            value={individualView?.firstName}
            readOnly={isReadOnly}
            error={(validationError || isReadOnly) && !individualView.firstName}
            helperText={(validationError || isReadOnly) && !individualView.firstName ? errors.INCOMPLETE_DATA_FIELD : ""}
            onChange={(event) => onValueChanged('firstName', event)} />
          <TextField
            label="Middle Name"
            placeholder="Enter text here"
            maxLength={75}
            showClearButton
            value={individualView?.middleName}
            readOnly={isReadOnly}
            onChange={(event) => onValueChanged('middleName', event)} />
          <TextField
            label="Last Name"
            placeholder="Enter text here"
            maxLength={75}
            showClearButton
            value={individualView?.lastName}
            readOnly={isReadOnly}
            error={(validationError || isReadOnly) && !individualView.lastName}
            helperText={(validationError || isReadOnly) && !individualView.lastName ? errors.INCOMPLETE_DATA_FIELD : ""}
            onChange={(event) => onValueChanged('lastName', event)} />
        </Box>
        {validationError && <InfoPanel isWarning title={errors.INCOMPLETE_DATA} />}
      </Box>
    </DialogContent>
    {!isReadOnly && <Controls
      cancelText='Cancel'
      submitText={isAdd ? 'Add' : 'Update'}
      onCancelClick={dialogClose}
      onSubmitClick={handleSubmitPerson}
    />}
  </>;

  return (
    <Box>
      <Box display='flex' justifyContent='space-between' alignItems='center'>
        <SectionTitle noUnderline>
          Individuals
        </SectionTitle>
        {(!isReadOnly && !noControlPerson) && <Button variant='text' onClick={() => setDialog(true)}>
          Add Individual (Control Person)
        </Button>}
      </Box>
      {!noControlPerson && <>
        {(!individualsList || individualsList.length === 0) && <Typography variant='body2'>
          No Individuals have been added yet.
        </Typography>}
        <EntityLabelGroup>
          {individualsList.map((person, i) => {
            const isComplete = validateComplete(person);

            return (
              <EntityLabel
                key={i}
                title={personEntityTitle(person)}
                subtitle={isComplete ? 'Complete' : 'Enter Information'}
                icon={isComplete ? <UserCheck /> : <User />}
                actionIcon={!isReadOnly ? <Trash2 /> : undefined}
                isComplete={isComplete}
                isWarning={!isComplete}
                onEntityClick={() => handleEdit(person)}
                onActionClick={!isReadOnly ? () => handleRemoveClick(person) : undefined} />
            );
          })}
        </EntityLabelGroup>
      </>}
      {noControlPerson && <EntityLabel
        title='Not Available'
        subtitle='Complete'
        icon={<UserCheck />}
        isComplete />}
      <Dialog open={dialog} onClose={dialogClose} onTransitionExited={handleDialogClose}>
        <DialogTitle title={dialogTitle} onClose={dialogClose} />
        {slideView === FORM_VIEW && renderAddNew()}
        {isRemoveView && <RemovePerson
          person={{ ...individualView }}
          // selectedRemove={isIndividual}
          onCancel={dialogClose}
          onRemoveSubmit={handleRemove} />}
      </Dialog>
    </Box>
  );
};

IndividualControlPerson.propTypes = {
  noControlPerson: PropTypes.bool,
  onControlPersonAdd: PropTypes.func,
  onControlPersonUpdate: PropTypes.func,
  onControlPersonRemove: PropTypes.func
};

export default IndividualControlPerson;
